/**
 * Blog API service for fetching articles from backend
 */
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

export interface BlogArticle {
  id: string;
  title: string;
  description: string;
  content?: string;
  category: string;
  tags: string[];
  imageUrl: string;
  date: string;
  readTime: string;
  author?: string;
  featured?: boolean;
  aiGenerated?: boolean;
  views: number;
  slug: string;
}

export interface ArticleListResponse {
  articles: BlogArticle[];
  total: number;
  hasMore: boolean;
}

export interface CategoryInfo {
  name: string;
  icon: string;
  count: number;
}

export interface TagInfo {
  name: string;
  count: number;
}

class BlogApiService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = `${API_BASE_URL}/blog`;
  }

  /**
   * Get filtered and paginated articles
   */
  async getArticles(params?: {
    category?: string;
    tags?: string;
    search?: string;
    limit?: number;
    offset?: number;
    sort?: 'date' | 'views' | 'title';
    order?: 'asc' | 'desc';
  }): Promise<ArticleListResponse> {
    try {
      const response = await axios.get<ArticleListResponse>(`${this.baseUrl}/articles`, {
        params
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching articles:', error);
      // Fallback to static data if API fails
      return this.getFallbackArticles(params);
    }
  }

  /**
   * Get single article by ID or slug
   */
  async getArticle(idOrSlug: string): Promise<BlogArticle | null> {
    try {
      const response = await axios.get<BlogArticle>(`${this.baseUrl}/articles/${idOrSlug}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching article:', error);
      // Fallback to static data
      const fallback = await this.getFallbackArticles();
      return fallback.articles.find(a => a.id === idOrSlug || a.slug === idOrSlug) || null;
    }
  }

  /**
   * Get trending articles
   */
  async getTrendingArticles(limit = 5): Promise<BlogArticle[]> {
    try {
      const response = await axios.get<BlogArticle[]>(`${this.baseUrl}/articles/trending`, {
        params: { limit }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching trending articles:', error);
      const fallback = await this.getFallbackArticles();
      return fallback.articles.slice(0, limit);
    }
  }

  /**
   * Get related articles
   */
  async getRelatedArticles(articleId: string, limit = 3): Promise<BlogArticle[]> {
    try {
      const response = await axios.get<BlogArticle[]>(`${this.baseUrl}/articles/related/${articleId}`, {
        params: { limit }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching related articles:', error);
      return [];
    }
  }

  /**
   * Get all categories with counts
   */
  async getCategories(): Promise<Record<string, CategoryInfo>> {
    try {
      const response = await axios.get<Record<string, CategoryInfo>>(`${this.baseUrl}/categories`);
      return response.data;
    } catch (error) {
      console.error('Error fetching categories:', error);
      return this.getFallbackCategories();
    }
  }

  /**
   * Get popular tags
   */
  async getPopularTags(limit = 20): Promise<TagInfo[]> {
    try {
      const response = await axios.get<TagInfo[]>(`${this.baseUrl}/tags`, {
        params: { limit }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching tags:', error);
      return [];
    }
  }

  /**
   * Track article view
   */
  async trackView(articleId: string): Promise<void> {
    try {
      await axios.post(`${this.baseUrl}/articles/${articleId}/view`);
    } catch (error) {
      console.error('Error tracking view:', error);
    }
  }

  /**
   * Generate article with AI (admin only)
   */
  async generateArticle(params: {
    topic: string;
    category: string;
    tags?: string[];
    style?: string;
  }): Promise<BlogArticle> {
    const response = await axios.post<BlogArticle>(`${this.baseUrl}/articles/generate`, params);
    return response.data;
  }

  /**
   * Generate random legal article without user input
   */
  async generateRandomArticle(): Promise<{
    success: boolean;
    message: string;
    article?: {
      id: string;
      title: string;
      category: string;
    };
    error?: string;
  }> {
    const response = await axios.post(`${this.baseUrl}/generate-random-article`);
    return response.data;
  }

  /**
   * Fallback data when API is unavailable
   */
  private async getFallbackArticles(params?: any): Promise<ArticleListResponse> {
    // Import static data as fallback
    const { blogArticles } = await import('../data/blogArticles');
    
    let filtered = [...blogArticles];
    
    // Apply filters
    if (params?.category && params.category !== 'all') {
      filtered = filtered.filter(a => a.category === params.category);
    }
    
    if (params?.search) {
      const search = params.search.toLowerCase();
      filtered = filtered.filter(a => 
        a.title.toLowerCase().includes(search) ||
        a.description.toLowerCase().includes(search)
      );
    }
    
    // Apply pagination
    const offset = params?.offset || 0;
    const limit = params?.limit || 20;
    const paginated = filtered.slice(offset, offset + limit);
    
    return {
      articles: paginated.map(a => ({
        ...a,
        views: 0,
        slug: a.title.toLowerCase().replace(/ /g, '-').slice(0, 50),
        aiGenerated: false
      })) as BlogArticle[],
      total: filtered.length,
      hasMore: (offset + limit) < filtered.length
    };
  }

  private getFallbackCategories(): Record<string, CategoryInfo> {
    return {
      "all": { name: "Všechny články", icon: "📚", count: 12 },
      "family": { name: "Rodina a děti", icon: "👨‍👩‍👧‍👦", count: 2 },
      "business": { name: "Obchod", icon: "💼", count: 2 },
      "criminal": { name: "Trestní právo", icon: "⚖️", count: 1 },
      "property": { name: "Nemovitosti", icon: "🏠", count: 2 },
      "labor": { name: "Pracovní právo", icon: "👔", count: 1 },
      "consumer": { name: "Ochrana spotřebitele", icon: "🛡️", count: 1 },
      "finance": { name: "Finance a daně", icon: "💰", count: 1 },
      "inheritance": { name: "Dědictví", icon: "📜", count: 1 },
      "traffic": { name: "Dopravní právo", icon: "🚗", count: 1 }
    };
  }
}

// Export singleton instance
export const blogApi = new BlogApiService();

// Export hooks for React Query
export const useBlogArticles = (params?: Parameters<BlogApiService['getArticles']>[0]) => {
  return {
    queryKey: ['blog-articles', params],
    queryFn: () => blogApi.getArticles(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
  };
};

export const useBlogArticle = (idOrSlug: string) => {
  return {
    queryKey: ['blog-article', idOrSlug],
    queryFn: () => blogApi.getArticle(idOrSlug),
    staleTime: 10 * 60 * 1000, // 10 minutes
  };
};